import type { QueryClient } from '@tanstack/react-query';
import type { LiveRead } from '@api-client/liveReads';
import {
  liveHostKey,
  livePollsListKey,
  liveScreenBaseKey,
  writePollIntoCaches,
} from '@lib/cache/polls';
import { getQuestionEntity } from '@lib/cache/questions';
import type { LiveHostState, LiveScreenRead, LiveState } from '@models/api';

/**
 * Writes a live state the server just answered with (a move's `LiveHostState`, or a 409's `details.state`) into
 * every big-screen read and the host read of that poll at once, so the author's screens show it without waiting
 * for the next 2 s read. Reads in flight from before the move (screen and host) are cancelled first, so they
 * cannot land after and step back; a cached state with a newer `version` is kept.
 */
export const writeLiveStateIntoCaches = async (
  queryClient: QueryClient,
  code: string,
  state: LiveState,
  host?: LiveHostState,
): Promise<void> => {
  await Promise.all([
    queryClient.cancelQueries({ queryKey: liveScreenBaseKey(code) }),
    queryClient.cancelQueries({ queryKey: liveHostKey(code) }),
  ]);
  queryClient.setQueriesData<LiveRead<LiveScreenRead>>(
    { queryKey: liveScreenBaseKey(code) },
    prev => {
      if (!prev?.ok) return prev;
      if (prev.data.state.version > state.version) return prev;
      return { ok: true, data: { ...prev.data, state } };
    },
  );

  // The host view (FE-L1b) holds a plain `LiveHostState`: written whole when a move answered one, re-read when
  // only a refusal's state is known. Only when it is cached: no host read is started from the screen.
  if (queryClient.getQueryData(liveHostKey(code)) === undefined) return;
  if (host) {
    queryClient.setQueryData<LiveHostState>(liveHostKey(code), prev =>
      prev && prev.state.version > host.state.version ? prev : host,
    );
  } else {
    // A refusal's state shows at once (FE-L1b: "a 409 refreshes from details.state"); the rest of the host read
    // (`can_back_from_results`, the questions) follows from a fresh read.
    queryClient.setQueryData<LiveHostState>(liveHostKey(code), prev =>
      prev && prev.state.version < state.version ? { ...prev, state } : prev,
    );
    void queryClient.invalidateQueries({ queryKey: liveHostKey(code) });
  }
};

/**
 * After a move the server made (from any surface: the host view, L9b's host controls, the big screen): the poll's
 * new `live_state` and position go into every cached copy of its question, so the author's card follows (L5's
 * not-started card becomes the live card at Start, the live card stops at End, Back from the results makes it live
 * again), and Your live polls re-reads its chips. Only the question's own copies are written: the broad questions
 * feeds are never invalidated here (TECH-305: the meetverse feed can be refetched before the ARQ fan-out and drop
 * the author's prepended card).
 */
export const writeLiveMoveIntoQuestionCaches = (
  queryClient: QueryClient,
  host: Pick<LiveHostState, 'question_id' | 'state'>,
): void => {
  const poll = getQuestionEntity(queryClient, host.question_id)?.poll;
  const { live_state, current_position, closed_at } = host.state;
  if (
    poll &&
    (poll.live_state !== live_state ||
      poll.current_position !== current_position ||
      poll.closed_at !== closed_at)
  ) {
    writePollIntoCaches(queryClient, host.question_id, {
      ...poll,
      live_state,
      current_position,
      closed_at,
    });
  }
  void queryClient.invalidateQueries({ queryKey: livePollsListKey() });
};
